import { InflateRequestMode } from '@sccs/types';
import { InferOutput } from 'valibot';
import { Characteristics, Consumption, EcoRating } from '../schema/VehicleTileDataSchema';
import { localEmissionAndConsumptionData } from '../schema/dcp/DcpLocalEmissionAndConsumptionData';
import { OptionalProductDto, UnitTypeDto } from '../types/dls/dls-response/ProductDto';
import { Co2Classes, EmissionAttribute, EmissionData, Vehicle } from '../types/one-search/queries';
import { TranslationType } from '../util/i18n';
import { COMMON_TECHNICAL_DATA_KEYS, MARKET_CONFIGS_DLS, MARKET_CONFIGS_ONE_SEARCH } from './TechnicalDataConfig';

type ParsedEmissionData = InferOutput<typeof localEmissionAndConsumptionData>;
type EmissionDataDls = ParsedEmissionData;
type EmissionDataOneSearch = Vehicle['emissionAndConsumption'];
type EmissionDataDlsAttribute = NonNullable<EmissionDataDls['uidata']['attributes']>[0];
type EmissionDataOneSearchAttribute = NonNullable<NonNullable<EmissionDataOneSearch>['attributes']>[0];

function isTruthy<T>(value: T | undefined | null): value is T {
  return value != undefined;
}

export type ParsedTechnicalData = {
  engineDescription: Characteristics;
  emissionData: Consumption[];
  ecoRating: EcoRating;
  emissionFootnotes: ParsedFootnotes;
  fuelType: Characteristics;
  gearBox: Characteristics;
  finalInspectionDate?: Characteristics;
};

export const ENGINE_ICON = 'bds/engine/24';
export const GEARBOX_ICON = 'bds/transmission/24';
export const GEARBOX_ID = 'gearboxId';
export const FINAL_INSPECTION_DATE_ICON = 'bds/service-calendar/24';
export const FINAL_INSPECTION_DATE_ID = 'finalInspectionDateId';

const FUEL_TYPE_ICONS: Record<string, string> = {
  ELECTRIC: 'bds/fuel-e-comp/24',
  PETROL_ELECTRIC_PLUGIN_HYBRID: 'fuel-hybrid/24',
  DIESEL_ELECTRIC_PLUGIN_HYBRID: 'fuel-hybrid/24',
  PETROL: 'bds/fuel-comp/24',
  DIESEL: 'bds/fuel-comp/24',
  DEFAULT: 'bds/fuel-comp/24',
};

export enum TypeContext {
  DLS = 'DLS',
  ONESEARCH = 'ONESEARCH',
}

export type OneSearchGraphQlVehicle = Vehicle & {
  responseTypeId: 'OneSearchGraphQlVehicle';
};

export type VehicleUnion = OptionalProductDto | OneSearchGraphQlVehicle;

const EMISSION_CLASS_IDS = ['166T', '260W']; // determines which ID has the emission class
const MUST_SHOW_TECHNICAL_DATA = ['SHORTOVERVIEW'];
const MUST_SHOW_IDS = ['166T', '85W']; // those IDs must be shown, nevertheless if they are in the mustshowin array
const INDICATES_CH_DATA_TO_BE_ADDED = ['244T', '260W'];
const INDICATES_CH_DATA_TO_BE_REMOVED = ['169T'];
const MARKETS_WITH_ENVKV = ['DE'];

export type ParsedFootnotes = Record<string, string> | null;
export type FuelTypeName = string | null;
export type EngineDescription = string | null;

export type TechnicalDataParser = {
  getTechnicalData: () => Partial<ParsedTechnicalData>;
};

export function technicalDataConfigurationForMarkets(
  key: keyof ParsedTechnicalData,
  market: string,
  context: TypeContext,
): boolean {
  const configMap = context === TypeContext.DLS ? MARKET_CONFIGS_DLS : MARKET_CONFIGS_ONE_SEARCH;
  const relevantConfig = configMap[market] || COMMON_TECHNICAL_DATA_KEYS;
  return relevantConfig.includes(key);
}

export function getEngineDescriptionDls(
  product: OptionalProductDto,
  translations: (key: string, dataset?: TranslationType) => string,
): Characteristics {
  const formatPower = (power: UnitTypeDto | undefined): string | null => {
    if (!power?.value) return null;
    let translatedUnit = translations(`dcp.unit.${power.unitCode}`) || power.unitCode;
    if (translatedUnit === `dcp.unit.${power.unitCode}` && power.unitCode) {
      // T4 already returns translated powerUnits, so we can't fallback to the translationKey here
      translatedUnit = power.unitCode;
    }
    return `${power.value} ${translatedUnit}`;
  };

  const combinePowers = (powers: (string | null)[]): string | undefined =>
    powers.filter(Boolean).join(' + ') || undefined;

  const { combustionPower, combustionPowerSecondary, electricPower, electricPowerSecondary } = product;

  if (combustionPower?.value === 0 && electricPower?.value === 0) {
    throw new Error('Combustion power and electric power cannot both be 0');
  }

  const primaryPower = combinePowers([formatPower(combustionPower), formatPower(electricPower)]);

  const secondaryPower = combinePowers([formatPower(combustionPowerSecondary), formatPower(electricPowerSecondary)]);

  const engineInformationString = secondaryPower ? `${primaryPower} (${secondaryPower})` : primaryPower || '';

  return {
    icon: ENGINE_ICON,
    value: engineInformationString,
    footnotes: getEngineFootnotes(product, translations),
    id: 'engineId',
  };
}

export const getEcoRating = (product: OptionalProductDto, language: string): EcoRating => {
  if (!product.localEmissionAndConsumptionData) return {};
  const emissionData: ParsedEmissionData = JSON.parse(product.localEmissionAndConsumptionData);
  const ecoRatingImage = emissionData.uidata?.attributes?.find((el) => el.displayImage)?.displayImage ?? {};
  // emission is only needed for France at the moment. 85W is the id for combustion engines, 105W for hybrid vehicles and ZEROCO2 for electric vehicles
  const emission = emissionData.uidata?.attributes?.find(
    (el) => el.id === '85W' || el.id == '105W' || el.id == 'ZEROCO2',
  )?.value;
  return {
    image: { url: ecoRatingImage[language] },
    emission: emission ? parseFloat(emission) : undefined,
  };
};

export function getEngineDescriptionOneSearch(
  product: OneSearchGraphQlVehicle,
  translations: (key: string, dataset?: TranslationType) => string,
): Characteristics {
  if (isOneSearchGraphQlVehicle(product)) {
    return {
      icon: ENGINE_ICON,
      value: product?.technicalInformation?.engine?.power?.formattedValue as string,
      footnotes: getEngineFootnotes(product, translations),
      id: 'engineId',
    };
  }
  throw new Error('getEngineDescriptionOneSearch Error');
}

export function getGearbox(product: VehicleUnion, context: TypeContext): Characteristics {
  switch (context) {
    case TypeContext.DLS:
      if (isVehicleDls(product)) {
        return {
          icon: GEARBOX_ICON,
          value: product?.gearBox?.name,
          id: GEARBOX_ID,
          key: product?.gearBox?.code,
        };
      }
      throw new Error('Invalid product type for DLS context');
    case TypeContext.ONESEARCH:
      if (isOneSearchGraphQlVehicle(product)) {
        return {
          icon: 'gear',
          value: product?.technicalInformation?.transmission?.formattedValue as string,
          id: GEARBOX_ID,
          key: product?.technicalInformation?.transmission?.value as string,
        };
      }
      throw new Error('Invalid product type for ONESEARCH context');
    default:
      throw new Error('Invalid context for getGearbox');
  }
}

export function getFuelType(product: VehicleUnion, context: TypeContext): Characteristics {
  switch (context) {
    case TypeContext.DLS:
      if (isVehicleDls(product)) {
        return {
          icon: product?.fuelType?.code ? FUEL_TYPE_ICONS[product?.fuelType?.code] : FUEL_TYPE_ICONS.DEFAULT,
          value: product?.fuelType?.name,
          id: 'fuelTypeId',
          key: product?.fuelType?.code,
        };
      }
      throw new Error('Invalid product type for DLS context');
    case TypeContext.ONESEARCH:
      if (isOneSearchGraphQlVehicle(product)) {
        const fuelTypeValue = product?.technicalInformation?.engine?.fuelType?.value as string;
        return {
          icon: fuelTypeValue ? FUEL_TYPE_ICONS[fuelTypeValue] : FUEL_TYPE_ICONS.DEFAULT,
          value: product?.technicalInformation?.engine?.fuelType?.formattedValue as string,
          id: 'fuelTypeId',
          key: fuelTypeValue,
        };
      }
      throw new Error(`Invalid product type for ONESEARCH context ${product}`);
    default:
      throw new Error('Invalid context for getFuelType');
  }
}

export function getFinalInspectionDate(
  product: VehicleUnion,
  mode: InflateRequestMode,
  context: TypeContext,
): Characteristics | undefined {
  switch (context) {
    case TypeContext.DLS:
      if (mode === InflateRequestMode.SIMPLE_PRICE || mode === InflateRequestMode.BANNER_CAMPAIGN) return undefined;
      if (isVehicleDls(product) && product.finalInspectionDate) {
        return {
          icon: FINAL_INSPECTION_DATE_ICON,
          value: convertDateToLocaleString(product.finalInspectionDate),
          id: FINAL_INSPECTION_DATE_ID,
        };
      }
      throw new Error('Invalid product type for DLS context or no finalInspectionDate provided');
    case TypeContext.ONESEARCH:
      if (mode === InflateRequestMode.SIMPLE_PRICE || mode === InflateRequestMode.BANNER_CAMPAIGN) return undefined;
      if (isOneSearchGraphQlVehicle(product)) {
        return {
          icon: FINAL_INSPECTION_DATE_ICON,
          value: convertDateToLocaleString(product.productionDate as string),
          id: FINAL_INSPECTION_DATE_ID,
        };
      } else {
        throw new Error('Characteristic getFinalInspectionDate not supported for FoC REST client');
      }
    default:
      throw new Error('Invalid context for getFinalInspectionDate');
  }
}

export const getEmissionFootnotesMap = (product: OptionalProductDto): Record<string, string> | null => {
  if (!product.localEmissionAndConsumptionData) return null;

  const emissionData: ParsedEmissionData = JSON.parse(product.localEmissionAndConsumptionData);
  return emissionData.uidata?.footnotes ?? null;
};

export const getEmissionFootnotesString = (product: OneSearchGraphQlVehicle): string | null => {
  try {
    if (isOneSearchGraphQlVehicle(product)) {
      const emissionData = product.emissionAndConsumption;
      if (!product.emissionAndConsumption || !emissionData?.footnotes) return null;
      return emissionData?.footnotes;
    }
    throw new Error('Error parsing emission data');
  } catch (e) {
    console.error('Error parsing emission data:', e);
    throw new Error('Error parsing emission data');
  }
};

export const getEngineFootnotes = (
  product: VehicleUnion,
  translations: (key: string, dataset?: TranslationType) => string,
): string[] => {
  const isHybridDls = isVehicleDls(product) && !(product.combustionPower?.value && !product.electricPower?.value);
  const isHybridOneSearch =
    isOneSearchGraphQlVehicle(product) &&
    !(
      product?.technicalInformation?.engine?.power?.combustionHp?.value &&
      !product?.technicalInformation?.engine?.power?.electricHp?.value
    );
  if (
    !!translations('dcp.srp.footnotes.powerHybrid') &&
    translations('dcp.srp.footnotes.powerHybrid') !== 'dcp.srp.footnotes.powerHybrid' &&
    (isHybridDls || isHybridOneSearch)
  ) {
    return [translations('dcp.srp.footnotes.powerHybrid')];
  }
  return [];
};

export function getEmissionData(
  product: VehicleUnion,
  context: TypeContext,
  language: string,
  translations: (key: string, dataset?: TranslationType) => string,
  market: string,
): Consumption[] {
  switch (context) {
    case TypeContext.DLS:
      if (isVehicleDls(product)) {
        if (!product.localEmissionAndConsumptionData) return [];
        try {
          const emissionData: ParsedEmissionData = JSON.parse(product.localEmissionAndConsumptionData);
          const attributes = getRelevantAttributes(emissionData, context, market) as EmissionDataDlsAttribute[];

          // Add CO2 emissions for electric vehicles in Switzerland manually until provided by DLS
          if (product?.fuelType?.code === 'ELECTRIC' && market === 'CH') {
            attributes.push({
              id: '182T',
              label: {
                de: 'CO₂-Emissionen kombiniert',
                it: 'Emissioni di CO₂ combinate',
                fr: 'Emissions de CO₂ mixtes',
              },
              displayValue: '0 g/km',
              value: '0',
              unit: 'g/km',
              order: 2,
              displayImage: undefined,
            });
          }

          const emissionFootnotes = getEmissionFootnotesMap(product);

          const attributeConsumptions = mapAttributesToConsumptionDls(attributes, language, emissionFootnotes);

          const co2ClassConsumptions = addCO2ClassesIfAvailable(emissionData, translations);
          if (co2ClassConsumptions.length < 1 && MARKETS_WITH_ENVKV.includes(market))
            throw Error(`Market: ${market} needs the ENVKV label, but it is missing.`);

          const mergedConsumption = [...attributeConsumptions, ...co2ClassConsumptions];

          return mergedConsumption;
        } catch (e) {
          console.error(e);
          throw e;
        }
      }
      break;
    case TypeContext.ONESEARCH:
      if (isOneSearchGraphQlVehicle(product)) {
        if (!product.technicalInformation) return [];
        try {
          const emissionData = product.emissionAndConsumption as EmissionData;
          const envkv = product.envkv?.co2Classes as Co2Classes;
          const attributes = getRelevantOneSearchGraphQlAttributes(
            emissionData,
            market,
          ) as EmissionDataOneSearchAttribute[];
          const emissionFootnotes = getEmissionFootnotesString(product);

          const attributeConsumptions = attributes
            .map((attr) => {
              return mapAttributeToConsumptionOneSearch(attr, emissionFootnotes);
            })
            .filter(isTruthy);

          const co2ClassConsumptions = createCo2ClassConsumptions(envkv, translations);
          if (co2ClassConsumptions.length < 1 && MARKETS_WITH_ENVKV.includes(market))
            throw Error(`Market: ${market} needs the ENVKV label, but it is missing.`);

          const mergedConsumption = [...attributeConsumptions, ...co2ClassConsumptions];

          return mergedConsumption;
        } catch (e) {
          console.error(e);
          throw e;
        }
      }
      break;
    default:
      throw new Error('Invalid context for getEmissionData');
  }
  return [];
}

function getRelevantAttributes(
  emissionData: EmissionDataDls,
  context: TypeContext,
  market: string,
): EmissionDataDlsAttribute[] {
  switch (context) {
    case TypeContext.DLS:
      return (
        (emissionData as EmissionDataDls).uidata?.attributes?.filter((attribute) => {
          return isRelevantAttributeDls(attribute, market);
        }) || []
      );
    default:
      return [];
  }
}

function getRelevantOneSearchGraphQlAttributes(emissionData: EmissionData, market: string): EmissionAttribute[] {
  return (
    emissionData?.attributes?.filter((attribute) => {
      return isRelevantAttributeOneSearchGraphQl(attribute, market);
    }) || []
  );
}

function isRelevantAttributeDls(el: EmissionDataDlsAttribute, market: string): boolean {
  const mustShowInNotSet = !el.mustshowin;
  const showBecauseTechnicalData = MUST_SHOW_TECHNICAL_DATA.some((t) => el.mustshowin?.includes(t));
  const showBecauseId = MUST_SHOW_IDS.some((id) => el.id === id);
  const showInCH = market === 'CH' && INDICATES_CH_DATA_TO_BE_ADDED.includes(el.id);
  const ignoreInCH = market === 'CH' && INDICATES_CH_DATA_TO_BE_REMOVED.includes(el.id);

  return (mustShowInNotSet || showBecauseTechnicalData || showBecauseId || showInCH) && !ignoreInCH;
}

function isRelevantAttributeOneSearchGraphQl(el: EmissionAttribute, market: string): boolean {
  const mustShowInNotSet = !el.mustShowIn;
  const showBecauseTechnicalData =
    el.id !== undefined && MUST_SHOW_TECHNICAL_DATA.some((t) => el.mustShowIn?.includes(t));
  const showBecauseId = el.id !== undefined && MUST_SHOW_IDS.some((id) => el.id === id);
  const showInCH = market === 'CH' && el.id !== undefined && INDICATES_CH_DATA_TO_BE_ADDED.includes(el.id as string);
  const ignoreInCH =
    market === 'CH' && el.id !== undefined && INDICATES_CH_DATA_TO_BE_REMOVED.includes(el.id as string);

  return (mustShowInNotSet || showBecauseTechnicalData || showBecauseId || showInCH) && !ignoreInCH;
}

function mapAttributesToConsumptionDls(
  attributes: (EmissionDataDlsAttribute | undefined)[],
  language: string,
  emissionFootnotes: Record<string, string> | null,
): Consumption[] {
  const convertedAttributes = attributes.map((attr) => {
    if (!attr) return;
    return {
      label: attr.label[language.toLowerCase()],
      value: attr.displayValue,
      id: attr.id,
      isEnergyEfficiency: EMISSION_CLASS_IDS.some((id) => attr.id === id),
      footnotes: emissionFootnotes?.[language] ? [emissionFootnotes[language]] : undefined,
    };
  });
  return convertedAttributes.filter(isTruthy);
}

function mapAttributeToConsumptionOneSearch(
  attr: EmissionDataOneSearchAttribute,
  emissionFootnotes: string | null,
): Consumption {
  return {
    label: attr.label as string,
    value: attr.displayValue as string,
    id: attr.id as string,
    isEnergyEfficiency: EMISSION_CLASS_IDS.some((id) => attr.id === id),
    footnotes: emissionFootnotes ? [emissionFootnotes] : undefined,
  };
}

function addCO2ClassesIfAvailable(
  emissionData: ParsedEmissionData,
  translations: (key: string, dataset?: TranslationType) => string,
): Consumption[] {
  const co2Classes = emissionData.envkv?.co2Classes;
  const consumptions: Consumption[] = [];

  if (co2Classes?.primary) {
    consumptions.push(createCO2ClassConsumption('primary', co2Classes.primary, translations));
  }

  if (co2Classes?.secondary) {
    consumptions.push(createCO2ClassConsumption('secondary', co2Classes.secondary, translations));
  }

  return consumptions;
}

function createCo2ClassConsumptions(
  co2Classes: Co2Classes,
  translations: (key: string, dataset?: TranslationType) => string,
): Consumption[] {
  const consumptions: Consumption[] = [];

  if (co2Classes?.primary) {
    consumptions.push(createCO2ClassConsumption('primary', co2Classes.primary, translations));
  }

  if (co2Classes?.secondary) {
    consumptions.push(createCO2ClassConsumption('secondary', co2Classes.secondary, translations));
  }

  return consumptions;
}

function createCO2ClassConsumption(
  type: 'primary' | 'secondary',
  value: string,
  translations: (key: string, dataset?: TranslationType) => string,
): Consumption {
  return {
    label: translations(`vehicleTile.envkv.co2Classes.${type}.label`, TranslationType.ONE_TEASER),
    value,
    isEnergyEfficiency: true,
    id: `dls_co2_class_${type}`,
  };
}

function convertDateToLocaleString(date: string): string {
  const formattedDate = new Date(date);
  if (isNaN(formattedDate.getTime())) {
    throw new Error('Invalid date string');
  }
  return formattedDate.toLocaleDateString('en-US', {
    year: 'numeric',
    month: '2-digit',
  });
}

// Type Guards
function isVehicleDls(product: VehicleUnion): product is OptionalProductDto {
  return product.responseTypeId === 'ProductDto';
}

function isOneSearchGraphQlVehicle(product: VehicleUnion): product is OneSearchGraphQlVehicle {
  return product.responseTypeId === 'OneSearchGraphQlVehicle';
}
